import { Card, Title, BarList, AreaChart, BarChart, Metric, Text, Flex, Grid } from '@tremor/react';
import { FilterBar, buildQueryString, useFilters } from './Filters';
import { useApi } from './useApi';

type SurveyAggregate = {
  survey_key: string;
  sent: number;
  completed: number;
  response_rate: number;
  promoters: number;
  passives: number;
  detractors: number;
  nps: number | null;
  sentiment_avg: number | null;
};
type SentimentTrendRow = { day: string; sentiment_avg: number | null; responses: number };
type DockRow = { dock_id: string | null; dock_name: string | null; completed: number; nps: number | null };
type ThemeRow = { theme: string; mention_count: number };

type Overview = {
  surveys: SurveyAggregate[];
  sentiment_trend: SentimentTrendRow[];
  docks: DockRow[];
  themes: ThemeRow[];
};

type DocksList = { docks: Array<{ id: string; name: string; tz: string }> };

const SURVEY_LABEL: Record<string, string> = {
  post_sale: 'Post-sale',
  post_trip: 'Post-trip',
  post_training: 'Post-training',
};

export function DashboardPage(): JSX.Element {
  const [filters, setFilters] = useFilters();
  const overview = useApi<Overview>(`/api/admin/metrics/overview?${buildQueryString(filters)}`, [JSON.stringify(filters)]);
  const docks = useApi<DocksList>('/api/admin/metrics/docks');

  return (
    <div>
      <Flex alignItems="center" justifyContent="between" className="mb-4">
        <Title>Dashboard</Title>
      </Flex>

      <FilterBar
        value={filters}
        onChange={setFilters}
        docks={docks.kind === 'ready' ? docks.data.docks : []}
      />

      {overview.kind === 'loading' && <Text>Loading…</Text>}
      {overview.kind === 'error' && <Text>Couldn't load metrics ({overview.message})</Text>}
      {overview.kind === 'ready' && (
        <DashboardContent data={overview.data} />
      )}
    </div>
  );
}

function DashboardContent({ data }: { data: Overview }): JSX.Element {
  const fallback = (s: SurveyAggregate): SurveyAggregate => ({ ...s, nps: s.nps ?? 0, sentiment_avg: s.sentiment_avg ?? 0 });
  const surveys = data.surveys.map(fallback);

  return (
    <>
      {/* KPI tiles */}
      <Grid numItemsSm={1} numItemsMd={3} className="gap-4 mb-6">
        {(['post_sale', 'post_trip', 'post_training'] as const).map((key) => {
          const s = surveys.find((x) => x.survey_key === key);
          return (
            <Card key={key}>
              <Text>{SURVEY_LABEL[key]}</Text>
              <Metric>
                {s ? `${Math.round(s.response_rate * 100)}%` : '—'}
              </Metric>
              <Text className="mt-2">
                {s ? `${s.completed.toLocaleString()} of ${s.sent.toLocaleString()} sent · NPS ${formatNps(s.nps)}` : 'No sends'}
              </Text>
            </Card>
          );
        })}
      </Grid>

      {/* Sentiment trend */}
      <Card className="mb-6">
        <Title>Sentiment trend (90 days)</Title>
        {data.sentiment_trend.length === 0
          ? <Text className="mt-2">No completed responses in this window.</Text>
          : (
            <AreaChart
              className="h-72 mt-4"
              data={data.sentiment_trend.map((r) => ({
                day: r.day,
                sentiment: r.sentiment_avg ?? 0,
                responses: r.responses,
              }))}
              index="day"
              categories={['sentiment']}
              colors={['blue']}
              valueFormatter={(v) => v.toFixed(2)}
              yAxisWidth={48}
            />
          )}
      </Card>

      {/* Per-dock breakdown */}
      <Grid numItemsSm={1} numItemsMd={2} className="gap-4 mb-6">
        <Card>
          <Title>Completed responses per dock</Title>
          {data.docks.length === 0
            ? <Text className="mt-2">No completed responses.</Text>
            : (
              <BarChart
                className="h-72 mt-4"
                data={data.docks.map((d) => ({ dock: d.dock_name ?? '(none)', completed: d.completed, nps: d.nps ?? 0 }))}
                index="dock"
                categories={['completed']}
                colors={['blue']}
                yAxisWidth={48}
              />
            )}
        </Card>
        <Card>
          <Title>Top themes</Title>
          {data.themes.length === 0
            ? <Text className="mt-2">No themes — Phase 6 AI batch lights this up.</Text>
            : (
              <BarList
                className="mt-4"
                data={data.themes.slice(0, 12).map((t) => ({ name: t.theme, value: t.mention_count }))}
              />
            )}
        </Card>
      </Grid>
    </>
  );
}

function formatNps(n: number | null): string {
  if (n === null) return '—';
  return n.toFixed(0);
}
